'use client';

import { UnifiedIncidentCaptureAdmissionGate } from '@/components/compliance/incidents/UnifiedIncidentCaptureAdmissionGate';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { hrPageFenceKey } from '@/lib/workforce/hr-page-fence';
import { useModules } from '../contexts/module-context';
import { Card, CardContent, Button } from '@statura/ui';
import { Lock } from 'lucide-react';
import {
  isModulePathAccessible,
  isPayrollEntitlementPath,
  isSubscriptionBlockedPathAccessible,
} from '../lib/module-access';
import { isDashboardProductionSurfaceVisibleForTenant } from '../lib/production-surface-access';
import { AppLoadingState } from './app-loading-state';
import { MedicationModuleNotEnabled } from './medications/MedicationModuleNotEnabled';

function logModuleGateBlock(
  pathname: string,
  reason: string,
  enabledModules: string[] | null,
  details: Record<string, unknown> = {},
) {
  console.warn('[module-gate] blocked', {
    pathname,
    reason,
    enabledModulesState: enabledModules === null ? 'all' : 'explicit',
    enabledModulesCount: enabledModules?.length ?? null,
    enabledModulesSample: enabledModules?.slice(0, 12) ?? null,
    ...details,
  });
}

/**
 * Page-level module access gate.
 * Wraps dashboard children and checks the current pathname against the shared
 * module entitlement matcher.
 * If the module is not enabled, shows a "Module not available" page.
 * Legacy null enabledModules values bypass module gating, but never bypass
 * subscription lockout. New plan resolution should provide explicit modules.
 */
export function ModuleGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const legacy = <LegacyModuleGate>{children}</LegacyModuleGate>;
  return pathname === '/compliance/incidents/new' ? (
    <UnifiedIncidentCaptureAdmissionGate legacy={legacy} requireModuleAccess>
      {children}
    </UnifiedIncidentCaptureAdmissionGate>
  ) : (
    legacy
  );
}

function LegacyModuleGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const params = useSearchParams();
  // Hosted HR views have their own existing controlled-reader fence. Match it
  // in the shell too; the generic host grant cannot authorise the HR view.
  const modulePath = hrPageFenceKey(`${pathname}?${params?.toString() ?? ''}`);
  const {
    enabledModules,
    tenantId,
    isDemoTenant,
    demoPlanModules,
    isCiFree,
    modulesLoaded,
    isSubscriptionBlocked,
    moduleLoadError,
  } = useModules();
  const router = useRouter();
  // Still loading module config — show spinner, not error (prevents flash-of-error on login/navigation)
  if (!modulesLoaded) {
    return (
      <AppLoadingState
        variant="inline"
        title="Opening workspace"
        description="Checking module access for this page."
      />
    );
  }

  // Subscription lockout applies before module entitlement. A canceled
  // legacy tenants may still have enabledModules=null, but must not be able to
  // create tasks or start workflows when subscription-blocked.
  if (isSubscriptionBlocked && !isSubscriptionBlockedPathAccessible(pathname)) {
    logModuleGateBlock(pathname, 'subscription_blocked', enabledModules, {
      isSubscriptionBlocked,
    });
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md text-center">
          <CardContent>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-soft mb-4 mt-4">
              <Lock className="h-7 w-7 text-text-subtle" />
            </div>
            <h3 className="font-heading text-card-title text-text-heading">
              Subscription inactive
            </h3>
            <p className="mx-auto mt-2 max-w-xs font-body text-body-sm text-text-muted">
              Your subscription is no longer active. Please visit Settings &gt; Billing to
              reactivate your account.
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button variant="secondary" onClick={() => router.back()}>
                Go back
              </Button>
              <Button variant="primary" onClick={() => router.push('/settings/billing')}>
                Go to Billing
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    !isDashboardProductionSurfaceVisibleForTenant(pathname, {
      tenantId,
      isDemo: isDemoTenant,
    })
  ) {
    logModuleGateBlock(pathname, 'production_surface_hidden', enabledModules);
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md text-center">
          <CardContent>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-soft mb-4 mt-4">
              <Lock className="h-7 w-7 text-text-subtle" />
            </div>
            <h3 className="font-heading text-card-title text-text-heading">
              Coming soon for production tenants
            </h3>
            <p className="mx-auto mt-2 max-w-xs font-body text-body-sm text-text-muted">
              This area is still being rolled out for production release — it&apos;s included in
              your plan and will be enabled shortly.
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button variant="secondary" onClick={() => router.back()}>
                Go back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Backward-compatible fallback for legacy tenants that still resolve to null.
  // Demo all-access is that same null token — it does not unlock payroll.
  if (!enabledModules && !(isDemoTenant && isPayrollEntitlementPath(pathname))) {
    return <>{children}</>;
  }

  const isEnabled = isModulePathAccessible(modulePath, enabledModules, {
    isDemoTenant,
    demoPlanModules,
  });

  if (isEnabled) return <>{children}</>;

  // The shell falls back to `enabledModules = []` when the module configuration
  // could not be READ, which is indistinguishable here from "read fine, this page
  // is not licensed". Claiming a plan limitation in that case is a wrong answer
  // that sends the provider to billing for what is a data-visibility failure —
  // observed live on 2026-07-25 for a PAYROLL_OFFICER whose own tenant admin
  // could open the same page. Say what we actually know, and offer a retry.
  if (moduleLoadError) {
    logModuleGateBlock(pathname, 'module_config_unavailable', enabledModules, {
      isCiFree,
      isSubscriptionBlocked,
    });
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md text-center">
          <CardContent>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-soft mb-4 mt-4">
              <Lock className="h-7 w-7 text-text-subtle" />
            </div>
            <h3 className="font-heading text-card-title text-text-heading">
              Access configuration unavailable
            </h3>
            <p className="mx-auto mt-2 max-w-xs font-body text-body-sm text-text-muted">
              We could not load your access configuration, so this page cannot be shown. This is not
              a change to your plan. Try again — if it keeps happening, contact your administrator
              and quote this page address.
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button variant="secondary" onClick={() => router.back()}>
                Go back
              </Button>
              <Button variant="primary" onClick={() => window.location.reload()}>
                Try again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  logModuleGateBlock(pathname, 'module_not_enabled', enabledModules, {
    isCiFree,
    isSubscriptionBlocked,
  });

  if (pathname === '/medications' || /^\/clients\/[^/]+\/medications$/.test(pathname)) {
    return <MedicationModuleNotEnabled title="Medications" />;
  }

  // Module not available — show friendly message
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-md text-center">
        <CardContent>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-soft mb-4 mt-4">
            <Lock className="h-7 w-7 text-text-subtle" />
          </div>
          <h3 className="font-heading text-card-title text-text-heading">
            {isSubscriptionBlocked ? 'Subscription inactive' : 'Module not available'}
          </h3>
          <p className="mx-auto mt-2 max-w-xs font-body text-body-sm text-text-muted">
            {isSubscriptionBlocked
              ? 'Your subscription is no longer active. Please visit Settings > Billing to reactivate your account.'
              : isCiFree
                ? 'This module is not included in your current plan. You are on the CI Free trial — only Continuous Improvement is available.'
                : 'This module is not included in your current plan. Contact your administrator to enable it.'}
          </p>
          <div className="mt-6 flex gap-2 justify-center">
            <Button variant="secondary" onClick={() => router.back()}>
              Go back
            </Button>
            {isSubscriptionBlocked && (
              <Button variant="primary" onClick={() => router.push('/settings/billing')}>
                Go to Billing
              </Button>
            )}
            {isCiFree && !isSubscriptionBlocked && (
              <Button
                variant="primary"
                onClick={() => router.push('/compliance/continuous-improvement?view=dashboard')}
              >
                Review continuous improvement
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
